import { useState } from "react";
import { Compass, TrendingUp } from "lucide-react";
import FeedList from "@/components/FeedList";
import { EP } from "@/lib/endpoints";
import { OpinionBubbleIcon } from "@/components/OpinionBubble";
import {
  CursiveHeader,
  DiscoverSearchBar,
  SegmentedTabs,
} from "@/pages/app/Discover";

/**
 * Opinions — 1:1 port of iOS `src/app/(app)/(tabs)/opinions.tsx`.
 *
 * Opinion-only feeds, split into:
 *   • Explore   → `feed/discover?post_type=opinion`
 *   • Trending  → `feed/trending?post_type=opinion`
 *
 * The layout mirrors Discover except the header pairs the cursive
 * "Opinions" title with a small OpinionBubble icon.
 */
export default function Opinions() {
  const [tab, setTab] = useState("explore");

  const header = (
    <>
      <CursiveHeader
        title="Opinions"
        subtitle="Agree or share a different view."
        right={<OpinionBubbleIcon size={22} className="text-foreground" />}
      />
      <div className="px-4">
        <DiscoverSearchBar testId="opinions-search" />
        <div className="mt-3 mb-1">
          <SegmentedTabs
            value={tab}
            onChange={setTab}
            options={[
              { key: "explore", label: "Explore", Icon: Compass },
              { key: "trending", label: "Trending", Icon: TrendingUp },
            ]}
            testId="opinions-tabs"
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="w-full" data-testid="opinions-screen">
      {tab === "explore" ? (
        <FeedList
          key="opinions-explore"
          endpointFn={EP.feedDiscoverOpinion}
          testid="feed-opinions-discover"
          header={header}
          fallbackToOwnPosts={false}
          emptyState={
            <div className="px-6 py-16 flex flex-col items-center text-center gap-3">
              <Compass size={28} className="text-muted-foreground" />
              <h3 className="text-foreground text-[17px] font-bold">
                No opinions to explore yet.
              </h3>
              <p className="text-muted-foreground text-[14px]">
                Fresh opinions from across BrontoY will appear here.
              </p>
            </div>
          }
        />
      ) : (
        <FeedList
          key="opinions-trending"
          endpointFn={EP.feedTrendingOpinion}
          testid="feed-opinions-trending"
          header={header}
          fallbackToOwnPosts={false}
          emptyState={
            <div className="px-6 py-16 flex flex-col items-center text-center gap-3">
              <TrendingUp size={28} className="text-muted-foreground" />
              <h3 className="text-foreground text-[17px] font-bold">
                No trending opinions right now.
              </h3>
              <p className="text-muted-foreground text-[14px]">
                Opinions gaining traction rise to the top here.
              </p>
            </div>
          }
        />
      )}
    </div>
  );
}
